// Карточка этажа (замечание владельца 2026-09-08): открывается кликом по номеру
// этажа в «Модели ГПР» — те же поля, что в справочнике «Этажи», чтобы
// не уходить с модели ради типа этажа или пуска тепла.
import { useCallback, useEffect, useState } from 'react';
import { call, p, type Data } from './api';
import { TIPY_ETAZHA, fmt, parseDate } from './util';

type Floor = Data<'/api/projects/{pid}/structure', 'get'>[string][number];

export function FloorCard({ pid, korpus, etazh, can, vstroennaya = false, onClose, onChanged }: {
  pid: number; korpus: string; etazh: string; can: boolean;
  /** внутри карточки задачи (вкладка «Этаж», владелец 2026-09-15): без окна и заголовка */
  vstroennaya?: boolean;
  onClose: () => void; onChanged?: (msg?: string) => void;
}) {
  const [floor, setFloor] = useState<Floor | null>(null);
  const [err, setErr] = useState('');
  const [pusk, setPusk] = useState('');
  const [kont, setKont] = useState('');

  const load = useCallback(async () => {
    const st = await call<Record<string, Floor[]>>('get', p('/api/projects/{pid}/structure', { pid }));
    if (!st.ok) { setErr(st.error.message); return; }
    const f = (st.data[korpus] || []).find(x => String(x.etazh) === String(etazh)) || null;
    setFloor(f);
    setPusk(fmt(f?.pusk) || '');
    setKont(fmt(f?.kontur_data) || '');
  }, [pid, korpus, etazh]);
  useEffect(() => { void load(); }, [load]);

  async function patch(pole: string, znachenie: string | number | boolean | null, soobshchenie?: string) {
    if (!floor) return;
    const r = await call<{ sozdano_zadach?: number; udaleno_zadach?: number }>(
      'patch', p('/api/floors/{fid}', { fid: floor.id }), { [pole]: znachenie, rev: floor.rev });
    if (!r.ok) { setErr(r.error.message); if (r.error.code === 'stale') await load(); return; }
    setErr('');
    await load();
    onChanged?.(soobshchenie);
  }

  const pole = (label: string, uzel: JSX.Element, hint?: string) => (
    <div className="wc-pole" key={label} title={hint}>
      <span className="wc-lab">{label}</span>
      {uzel}
    </div>
  );
  const vvod = (znach: string | number | null | undefined, na: string) => (
    <input defaultValue={znach ?? ''} disabled={!can}
      onBlur={e => { const v = e.target.value.trim(); if (v !== String(znach ?? '')) void patch(na, v || null); }} />
  );
  const spisok = (znach: string | null | undefined, na: string, list: string[], soobshchenie?: string) => (
    <select value={znach ?? ''} disabled={!can} onChange={e => void patch(na, e.target.value || null, soobshchenie)}>
      <option value="">— не задано —</option>
      {list.map(v => <option key={v}>{v}</option>)}
    </select>
  );

  const soderzhimoe = (
      <>
        {floor && (
          <div className="wc-info">
            Корпус <b>{korpus}</b> · этаж <b>{etazh}</b> · тип этажа: <b>{floor.tip || 'не задан'}</b>
            {floor.kvartir ? ' · квартир: ' + floor.kvartir : ''}
            {floor.zadach != null ? ' · задач на этаже: ' + floor.zadach : ''}
          </div>
        )}
        <div className="wc-body">
          {err && <div className="err">{err}</div>}
          {!floor && <div className="muted">Этаж не найден в структуре проекта.</div>}
          {floor && (
            <>
              <div className="wc-polya">
                {pole('Тип этажа', spisok(floor.tip, 'tip_etazha_gpr', TIPY_ETAZHA,
                  'Тип этажа изменён. Поэтажные задачи досозданы или убраны; нажмите «Актуализировать».'),
                  'Поэтажные работы ложатся только на типовые этажи: смена типа досоздаёт или убирает пустые задачи. «Исключение» — этаж вне расчёта: все его задачи уходят из модели')}
                {pole('Положение (номер)', vvod(floor.num, 'floor_num'),
                  'Номер для порядка: по нему этаж стоит среди соседей; у доп. этажей дробный («1т» между 1 и 2 = 1,5)')}
                {pole('Приоритет', vvod(floor.prioritet, 'avto_prioritet'),
                  'Порядок этажа в очереди бригады: больше — раньше, отрицательный — в конец; пусто — по номеру этажа')}
                {pole('Закрытие ТК', (
                  <span className="sb-sob">
                    <input value={kont} placeholder="ДД.ММ.ГГГГ" disabled={!can}
                      onChange={e => setKont(e.target.value)}
                      onBlur={() => {
                        if (kont.trim() === fmt(floor.kontur_data)) return;
                        const iso = kont.trim() === '' ? null : parseDate(kont);
                        if (iso === undefined) { setErr('Дата закрытия контура не разобрана: нужно ДД.ММ.ГГГГ'); return; }
                        void patch('kontur_data', iso);
                      }} />
                    <label title="Случилось. При пустой дате поставится сегодняшняя">
                      <input type="checkbox" disabled={!can} checked={!!floor.kontur_zakryt}
                        onChange={e => void patch('kontur_zakryt', e.target.checked)} /> готово
                    </label>
                  </span>
                ), 'Закрытие теплового контура — только для отчётности. Дата без отметки — план, с отметкой — факт')}
                {pole('Пуск тепла', (
                  <span className="sb-sob">
                    <input value={pusk} placeholder="ДД.ММ.ГГГГ" disabled={!can}
                      onChange={e => setPusk(e.target.value)}
                      onBlur={() => {
                        if (pusk.trim() === fmt(floor.pusk)) return;
                        const iso = pusk.trim() === '' ? null : parseDate(pusk);
                        if (iso === undefined) { setErr('Дата пуска тепла не разобрана: нужно ДД.ММ.ГГГГ'); return; }
                        void patch('pusk_tepla', iso);
                      }} />
                    <label title="Случилось. При пустой дате поставится сегодняшняя">
                      <input type="checkbox" disabled={!can} checked={!!floor.pusk_bylo}
                        onChange={e => void patch('pusk_bylo', e.target.checked)} /> готово
                    </label>
                  </span>
                ), 'Работы теплового контура не начнутся раньше этой даты. Дата без отметки — план, с отметкой — факт')}
                {pole('Комментарий', vvod(floor.kommentarij, 'kommentarij'))}
              </div>
              <div className="pw-muted" style={{ marginTop: 10 }}>
                Те же поля ведутся на экране «Этажи». После смены типа этажа нажмите
                «Актуализировать», чтобы обновить даты.
              </div>
            </>
          )}
        </div>
      </>
  );
  // внутри карточки задачи (вкладка «Этаж», 2026-09-15) — без своего окна и заголовка
  if (vstroennaya) return <div className="wc-vstroen">{soderzhimoe}</div>;
  return (
    <div className="dlg-fon" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dlg wc wc-sm" role="dialog" aria-modal="true"
        onKeyDown={e => { if (e.key === 'Escape') onClose(); }}>
        <div className="wc-top">
          <div className="wc-name">Корпус {korpus} · этаж {etazh}</div>
          <a className="wc-x" title="Закрыть (Esc)" onClick={onClose}>✕</a>
        </div>
        {soderzhimoe}
      </div>
    </div>
  );
}
